import { formatISO, max, min, parseISO } from 'date-fns'

export type FerrySpecificConditionType = 'FERRY' | 'FERRY_START' | 'FERRY_END'

type InfringementSpan = {
  infringementTimestamp: string
  periodStartDate?: string | null
  periodEndDate?: string | null
  activities: { datetimeStart: string; datetimeEnd: string }[]
}

/**
 * The span the infringement gantt draws: the period plus every activity the
 * backend returned around it. Ferry markers are fetched for this whole span,
 * so a crossing that started the evening before still shows its start pin.
 */
export function getFerryConditionsWindow(infringement: InfringementSpan) {
  const dates = [
    infringement.infringementTimestamp,
    infringement.periodStartDate,
    infringement.periodEndDate,
    ...infringement.activities.flatMap(activity => [
      activity.datetimeStart,
      activity.datetimeEnd,
    ]),
  ]
    .filter((value): value is string => !!value)
    .map(value => parseISO(value))

  return {
    dateStart: formatISO(min(dates)),
    dateEnd: formatISO(max(dates)),
  }
}

/**
 * The ferry button stores a generic FERRY row at the same instant as its
 * FERRY_START, which would stack two pins on top of each other.
 */
export function withoutDuplicateFerryEvents<
  T extends { type: FerrySpecificConditionType; timestamp: string },
>(conditions: T[]): T[] {
  const startTimes = new Set(
    conditions
      .filter(condition => condition.type === 'FERRY_START')
      .map(condition => parseISO(condition.timestamp).getTime())
  )
  return conditions.filter(
    condition =>
      condition.type !== 'FERRY' ||
      !startTimes.has(parseISO(condition.timestamp).getTime())
  )
}
